/**
 * FILE: packages/supabase/src/services/supportChat.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { ServiceError } from '../internal/errors'
import { logActivity, withActivityActor } from '../internal/activity'
import type { ActivityActor } from '../internal/activity'
import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";
import { getSupabaseAccessToken } from "../internal/authToken";
import { isDemoToken, studentPortalHeaders, type StudentPortalContext } from '../internal/studentPortal'
import { DEMO_PHONE, demoSupportConversation, demoSupportMessages } from './demoData'

export type SupportConversationStatus = 'open' | 'waiting' | 'escalated' | 'resolved'
export type SupportConversationCategory = 'support' | 'placements' | 'billing' | 'general'
export type SupportMessageType = 'text' | 'image' | 'file' | 'voice' | 'system' | 'note'
export type QuickReplyCategory = 'support' | 'placements' | 'billing' | 'general' | 'all'

type ConversationRow = Database['public']['Tables']['support_conversations']['Row']
type MessageRow = Database['public']['Tables']['support_messages']['Row']

// Local type for support_quick_replies until types are regenerated
interface QuickReplyRow {
  id: string
  label: string
  content: string
  category: string | null
  sort_order: number | null
  created_by: string
  created_at: string | null
  updated_at: string | null
}

/** Clean display type for the inbox list. */
export interface SupportConversationListItem {
  id: string
  phone: string
  assigned_to: string | null
  assigned_staff_name: string | null
  status: SupportConversationStatus
  category: SupportConversationCategory
  last_message_at: string
  unread_count: number
  created_at: string
  student_name: string
  course_code: string
  program: string
  valid_till: string | null
  attempt: string | null
  last_preview: string
}

/** Clean display type for messages. */
export interface SupportMessageRow {
  id: string
  conversation_id: string
  sender_type: 'student' | 'staff' | 'system'
  sender_id: string | null
  sender_name: string | null
  message_type: SupportMessageType
  content: string
  file_url: string | null
  file_name: string | null
  read_at: string | null
  edited_at: string | null
  created_at: string
}

export interface SupportMessagesPage {
  messages: SupportMessageRow[]
  hasMore: boolean
}

export interface QuickReplyItem {
  id: string
  label: string
  content: string
  category: QuickReplyCategory | null
  sort_order: number
}

const MESSAGE_PAGE_SIZE = 80

interface StudentJoin {
  name: string | null
  course: string | null
  program: string | null
  valid_till: string | null
  attempt: string | null
  phone?: string | null
}

function toConversationItem(
  c: ConversationRow & { assigned_staff: { name: string | null } | null },
  student: StudentJoin | null,
): SupportConversationListItem {
  return {
    id: c.id,
    phone: c.phone,
    assigned_to: c.assigned_to ?? null,
    assigned_staff_name: c.assigned_staff?.name ?? null,
    status: (c.status as SupportConversationStatus) ?? 'open',
    category: (c.category as SupportConversationCategory) ?? 'support',
    last_message_at: c.last_message_at ?? c.created_at ?? new Date().toISOString(),
    unread_count: c.unread_count ?? 0,
    created_at: c.created_at ?? new Date().toISOString(),
    student_name: student?.name ?? 'Unknown',
    course_code: student?.course ?? '',
    program: student?.program ?? '',
    valid_till: student?.valid_till ?? null,
    attempt: student?.attempt ?? null,
    last_preview: c.last_message_preview ?? '',
  }
}

function toMessageRow(m: MessageRow, staffName: string | null = null): SupportMessageRow {
  return {
    id: m.id,
    conversation_id: m.conversation_id,
    sender_type: (m.sender_type as SupportMessageRow['sender_type']) ?? 'system',
    sender_id: m.sender_id ?? null,
    sender_name: staffName,
    message_type: (m.message_type as SupportMessageType) ?? 'text',
    content: m.content,
    file_url: m.file_url ?? null,
    file_name: m.file_name ?? null,
    read_at: m.read_at ?? null,
    edited_at: m.edited_at ?? null,
    created_at: m.created_at ?? new Date().toISOString(),
  }
}

function toQuickReplyItem(r: QuickReplyRow): QuickReplyItem {
  return {
    id: r.id,
    label: r.label,
    content: r.content,
    category: (r.category as QuickReplyCategory) ?? 'all',
    sort_order: r.sort_order ?? 0,
  }
}

/** Fetch conversations with optional filters. Joins student data and assigned staff name. */
export async function getConversations(
  status?: SupportConversationStatus,
  assignedTo?: string | null,
  category?: SupportConversationCategory,
  limit = 100, client: DbClient = defaultDbClient
): Promise<SupportConversationListItem[]> {
  let query = client
    .from('support_conversations')
    .select('*, assigned_staff:staff!support_conversations_assigned_to_fkey(name)')
    .order('last_message_at', { ascending: false })
    .limit(limit)

  if (status) {
    query = query.eq('status', status)
  }

  if (assignedTo === null) {
    query = query.is('assigned_to', null)
  } else if (typeof assignedTo === 'string') {
    query = query.eq('assigned_to', assignedTo)
  }

  if (category) {
    query = query.eq('category', category)
  }

  const { data: convData, error: convError } = await query
  if (convError) {
    throw new ServiceError(convError.message, 'SUPPORT_CONVERSATIONS_FETCH', convError)
  }

  const conversations = (convData ?? []) as Array<
    ConversationRow & { assigned_staff: { name: string | null } | null }
  >
  if (conversations.length === 0) return []

  const phones = Array.from(new Set(conversations.map((c) => c.phone).filter((p): p is string => !!p)))
  let studentMap: Record<string, StudentJoin> = {}
  if (phones.length > 0) {
    const { data: studentData } = await client
      .from('students')
      .select('phone, name, course, program, valid_till, attempt')
      .in('phone', phones)
      .is('deleted_at', null)
    if (studentData) {
      studentMap = Object.fromEntries(studentData.map((s) => [s.phone, s as StudentJoin]))
    }
  }

  return conversations.map((c) => toConversationItem(c, studentMap[c.phone] ?? null))
}

/** Returns one page of messages in chronological order (oldest → newest). Page 0 is the latest slice.
 *  Fetches staff names in a second query because sender_id is text (stores phone for students, uuid for staff)
 *  and cannot be FK-joined directly to staff.id (uuid).
 */
export async function getMessages(
  conversationId: string,
  page = 0, client: DbClient = defaultDbClient
): Promise<SupportMessagesPage> {
  const { data, error } = await client
    .from('support_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .range(page * MESSAGE_PAGE_SIZE, (page + 1) * MESSAGE_PAGE_SIZE - 1)

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_MESSAGES_FETCH', error)
  }

  const raw = data ?? []
  const hasMore = raw.length === MESSAGE_PAGE_SIZE

  // Collect unique staff sender IDs to look up names
  const staffIdSet = new Set<string>()
  for (const m of raw) {
    if (m.sender_type === 'staff' && m.sender_id) {
      staffIdSet.add(m.sender_id)
    }
  }

  let staffNameMap: Record<string, string> = {}
  if (staffIdSet.size > 0) {
    const staffIds = Array.from(staffIdSet)
    const { data: staffData, error: staffError } = await client
      .from('staff')
      .select('id, name')
      .in('id', staffIds)

    if (!staffError && staffData) {
      staffNameMap = Object.fromEntries(staffData.map((s) => [s.id, s.name ?? 'Staff']))
    }
  }

  // Reverse so messages render oldest → newest within the page
  const messages = [...raw].reverse().map((m) =>
    toMessageRow(m as MessageRow, m.sender_type === 'staff' ? staffNameMap[m.sender_id!] ?? null : null)
  )

  return { messages, hasMore }
}

/** Send a text message. */
export async function sendMessage(
  conversationId: string,
  content: string,
  senderType: 'staff' | 'student' | 'system',
  senderId: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<SupportMessageRow> {
  const { data, error } = await client
    .from('support_messages')
    .insert({
      conversation_id: conversationId,
      sender_type: senderType,
      sender_id: senderId,
      content: content.trim(),
      message_type: 'text',
    })
    .select()
    .single()

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_SEND_MESSAGE', error)
  }
  if (!data) {
    throw new ServiceError('No message returned after insert', 'SUPPORT_SEND_MESSAGE')
  }

  await logActivity(withActivityActor(actor, {
    action: 'SEND_SUPPORT_MESSAGE',
    entityType: 'support_messages',
    entityId: data.id,
  }), client)
  return toMessageRow(data)
}

/** Send a message with an attached file. */
export async function sendMessageWithFile(
  conversationId: string,
  content: string,
  fileUrl: string,
  fileName: string,
  messageType: 'image' | 'file',
  senderType: 'staff' | 'student' | 'system',
  senderId: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<SupportMessageRow> {
  const { data, error } = await client
    .from('support_messages')
    .insert({
      conversation_id: conversationId,
      sender_type: senderType,
      sender_id: senderId,
      content: content.trim(),
      message_type: messageType,
      file_url: fileUrl,
      file_name: fileName,
    })
    .select()
    .single()

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_SEND_FILE', error)
  }
  if (!data) {
    throw new ServiceError('No message returned after insert', 'SUPPORT_SEND_FILE')
  }

  await logActivity(withActivityActor(actor, {
    action: 'SEND_SUPPORT_MESSAGE_WITH_FILE',
    entityType: 'support_messages',
    entityId: data.id,
  }), client)
  return toMessageRow(data)
}

/** Send an internal note (staff-only, not visible to students). */
export async function sendInternalNote(
  conversationId: string,
  content: string,
  staffId: string,
  staffName: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<SupportMessageRow> {
  const { data, error } = await client
    .from('support_messages')
    .insert({
      conversation_id: conversationId,
      sender_type: 'staff',
      sender_id: staffId,
      content: content.trim(),
      message_type: 'note',
    })
    .select()
    .single()

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_SEND_NOTE', error)
  }
  if (!data) {
    throw new ServiceError('No message returned after insert', 'SUPPORT_SEND_NOTE')
  }

  await logActivity(withActivityActor(actor, {
    action: 'SEND_SUPPORT_INTERNAL_NOTE',
    entityType: 'support_messages',
    entityId: data.id,
  }), client)
  const row = toMessageRow(data)
  row.sender_name = staffName
  return row
}

/** Mark all unread student messages in a conversation as read. */
export async function markMessagesRead(conversationId: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('support_messages')
    .update({ read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('sender_type', 'student')
    .is('read_at', null)

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_MARK_READ', error)
  }
  await logActivity(withActivityActor(actor, {
    action: 'MARK_SUPPORT_MESSAGES_READ',
    entityType: 'support_messages',
    entityId: conversationId,
  }), client)
}

export async function assignConversation(conversationId: string, staffId: string | null, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('support_conversations')
    .update({ assigned_to: staffId })
    .eq('id', conversationId)

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_ASSIGN', error)
  }
  await logActivity(withActivityActor(actor, {
    action: 'ASSIGN_SUPPORT_CONVERSATION',
    entityType: 'support_conversations',
    entityId: conversationId,
    newValues: { assigned_to: staffId },
  }), client)
}

export async function resolveConversation(conversationId: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('support_conversations')
    .update({ status: 'resolved' })
    .eq('id', conversationId)

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_RESOLVE', error)
  }
  await logActivity(withActivityActor(actor, {
    action: 'RESOLVE_SUPPORT_CONVERSATION',
    entityType: 'support_conversations',
    entityId: conversationId,
    newValues: { status: 'resolved' },
  }), client)
}

export async function reopenConversation(conversationId: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('support_conversations')
    .update({ status: 'open' })
    .eq('id', conversationId)

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_REOPEN', error)
  }
  await logActivity(withActivityActor(actor, {
    action: 'REOPEN_SUPPORT_CONVERSATION',
    entityType: 'support_conversations',
    entityId: conversationId,
    newValues: { status: 'open' },
  }), client)
}

export async function updateConversationCategory(
  conversationId: string,
  category: SupportConversationCategory,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('support_conversations')
    .update({ category })
    .eq('id', conversationId)

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_CATEGORY_UPDATE', error)
  }
  await logActivity(withActivityActor(actor, {
    action: 'UPDATE_SUPPORT_CONVERSATION_CATEGORY',
    entityType: 'support_conversations',
    entityId: conversationId,
    newValues: { category },
  }), client)
}

/* ───────── Quick Replies ───────── */

export async function getQuickReplies(category?: QuickReplyCategory, client: DbClient = defaultDbClient): Promise<QuickReplyItem[]> {
  let query = client
    .from('support_quick_replies')
    .select('*')
    .order('sort_order', { ascending: true })

  if (category && category !== 'all') {
    query = query.or(`category.eq.${category},category.eq.all`)
  }

  const { data, error } = await query
  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_QUICK_REPLIES_FETCH', error)
  }
  return (data ?? []).map(toQuickReplyItem)
}

export async function createQuickReply(
  payload: {
    label: string
    content: string
    category: QuickReplyCategory
    sort_order?: number
  },
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<QuickReplyItem> {
  const { data, error } = await client
    .from('support_quick_replies')
    .insert({
      label: payload.label.trim(),
      content: payload.content.trim(),
      category: payload.category,
      sort_order: payload.sort_order ?? 0,
      created_by: actor.userId,
    })
    .select()
    .single()

  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_QUICK_REPLY_CREATE', error)
  }
  if (!data) {
    throw new ServiceError('No quick reply returned after insert', 'SUPPORT_QUICK_REPLY_CREATE')
  }
  await logActivity(withActivityActor(actor, {
    action: 'CREATE_QUICK_REPLY',
    entityType: 'support_quick_replies',
    entityId: data.id,
    newValues: { label: payload.label, category: payload.category },
  }), client)
  return toQuickReplyItem(data)
}

export async function deleteQuickReply(id: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client.from('support_quick_replies').delete().eq('id', id)
  if (error) {
    throw new ServiceError(error.message, 'SUPPORT_QUICK_REPLY_DELETE', error)
  }
  await logActivity(withActivityActor(actor, {
    action: 'DELETE_QUICK_REPLY',
    entityType: 'support_quick_replies',
    entityId: id,
  }), client)
}

/** Upload a file to the support file edge function. */
export async function uploadSupportFile(
  file: File,
  conversationId: string
): Promise<{ url: string; name: string }> {
  const token = await getSupabaseAccessToken()
  if (!token) {
    throw new ServiceError('Not authenticated', 'SUPPORT_UPLOAD_AUTH')
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('conversationId', conversationId)

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-support-file`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    }
  )

  const result = (await res.json()) as { url?: string; name?: string; error?: string }
  if (!res.ok || result.error) {
    throw new ServiceError(result.error || 'Upload failed', 'SUPPORT_UPLOAD_FAILED')
  }
  if (!result.url || !result.name) {
    throw new ServiceError('Invalid upload response', 'SUPPORT_UPLOAD_INVALID')
  }
  return { url: result.url, name: result.name }
}

/* ───────── Student-facing support chat (edge functions) ───────── */

export interface StudentSupportMessage {
  id: string
  conversation_id: string
  sender_type: 'student' | 'staff' | 'system'
  sender_id: string
  content: string
  file_url: string | null
  file_name: string | null
  message_type: string | null
  created_at: string
  read_at: string | null
}

export interface StudentSupportConversation {
  id: string
  phone: string
  assigned_to: string | null
  status: string
  last_message_at: string | null
  unread_count: number
  created_at: string
}

export interface StudentSupportMessagesPage {
  messages: StudentSupportMessage[]
  hasMore: boolean
}

export async function getStudentSupportConversation(
  ctx: StudentPortalContext,
): Promise<StudentSupportConversation | null> {
  if (isDemoToken(ctx.token)) return demoSupportConversation as StudentSupportConversation
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/student-support?action=conversation`, {
    headers: studentPortalHeaders(ctx),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to fetch conversation' }))
    throw new ServiceError(err.error, 'STUDENT_SUPPORT_CONVERSATION')
  }
  const data = (await res.json()) as { conversation: StudentSupportConversation | null }
  return data.conversation
}

export async function getStudentSupportMessages(
  conversationId: string,
  page: number,
  ctx: StudentPortalContext,
): Promise<StudentSupportMessagesPage> {
  if (isDemoToken(ctx.token)) {
    return { messages: demoSupportMessages as StudentSupportMessage[], hasMore: false }
  }
  const res = await fetch(
    `${ctx.supabaseUrl}/functions/v1/student-support?action=messages&conversationId=${conversationId}&page=${page}`,
    { headers: studentPortalHeaders(ctx) }
  )
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to fetch messages' }))
    throw new ServiceError(err.error, 'STUDENT_SUPPORT_MESSAGES')
  }
  return (await res.json()) as StudentSupportMessagesPage
}

export async function sendStudentSupportMessage(
  conversationId: string,
  content: string,
  ctx: StudentPortalContext,
  file?: { url: string; name: string },
): Promise<StudentSupportMessage> {
  if (isDemoToken(ctx.token)) {
    return {
      id: `msg-${Date.now()}`,
      conversation_id: conversationId,
      sender_type: 'student',
      sender_id: DEMO_PHONE,
      content: content.trim(),
      file_url: file?.url ?? null,
      file_name: file?.name ?? null,
      message_type: null,
      created_at: new Date().toISOString(),
      read_at: null,
    } as StudentSupportMessage
  }
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/student-support?action=send`, {
    method: 'POST',
    headers: studentPortalHeaders(ctx),
    body: JSON.stringify({
      conversationId,
      content: content.trim(),
      fileUrl: file?.url,
      fileName: file?.name,
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send message' }))
    throw new ServiceError(err.error, 'STUDENT_SUPPORT_SEND')
  }
  const data = (await res.json()) as { message: StudentSupportMessage }
  return data.message
}

export async function markStudentSupportRead(
  conversationId: string,
  ctx: StudentPortalContext,
): Promise<void> {
  if (isDemoToken(ctx.token)) return
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/student-support?action=read`, {
    method: 'POST',
    headers: studentPortalHeaders(ctx),
    body: JSON.stringify({ conversationId }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to mark read' }))
    throw new ServiceError(err.error, 'STUDENT_SUPPORT_READ')
  }
}
